/**
 * Platform Admin Dashboard
 * 
 * Main dashboard showing system-wide metrics and overview
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface SystemStats {
  totalCompanies: number;
  totalUsers: number;
  activeUsers: number;
  totalRevenue: number;
  systemUptime: string;
  storageUsed: number;
  storageTotal: number;
}

interface RecentActivity {
  id: string;
  type: 'company_created' | 'user_registered' | 'security_alert' | 'system_update';
  message: string;
  timestamp: string;
  severity: 'info' | 'warning' | 'success' | 'danger';
}

export default function AdminDashboard() {
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      
      // Mock data - in real implementation, this would come from API
      const mockStats: SystemStats = {
        totalCompanies: 47,
        totalUsers: 1284,
        activeUsers: 892,
        totalRevenue: 2847392.50,
        systemUptime: '99.9%',
        storageUsed: 2.4,
        storageTotal: 10.0
      };

      const mockActivity: RecentActivity[] = [
        {
          id: '1',
          type: 'company_created',
          message: 'New company "TechCorp Solutions" registered',
          timestamp: '2024-01-28T10:30:00Z',
          severity: 'success'
        },
        {
          id: '2',
          type: 'security_alert',
          message: 'Multiple failed login attempts detected from IP 192.168.1.100',
          timestamp: '2024-01-28T09:15:00Z',
          severity: 'warning'
        },
        {
          id: '3',
          type: 'user_registered',
          message: '15 new users registered in the last hour',
          timestamp: '2024-01-28T08:45:00Z',
          severity: 'info'
        },
        {
          id: '4',
          type: 'system_update',
          message: 'System maintenance completed successfully',
          timestamp: '2024-01-28T06:00:00Z',
          severity: 'success'
        }
      ];

      setStats(mockStats);
      setRecentActivity(mockActivity);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'company_created':
        return 'bi-building';
      case 'user_registered':
        return 'bi-person-plus';
      case 'security_alert':
        return 'bi-shield-exclamation';
      case 'system_update':
        return 'bi-gear';
      default:
        return 'bi-info-circle';
    }
  };

  const getActivityColor = (severity: string) => {
    switch (severity) {
      case 'success':
        return 'text-success';
      case 'warning':
        return 'text-warning';
      case 'danger':
        return 'text-danger';
      default:
        return 'text-info';
    }
  };

  if (isLoading) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <div className="spinner-border admin-spinner mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <h5 className="text-muted">Loading dashboard...</h5>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h1 className="h3 fw-bold text-dark mb-1">Platform Dashboard</h1>
          <p className="text-muted mb-0">System overview and key metrics</p>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary">
            <i className="bi bi-arrow-clockwise me-1"></i>
            Refresh
          </button>
          <Link to="/analytics" className="btn btn-admin">
            <i className="bi bi-graph-up me-1"></i>
            View Analytics
          </Link>
        </div>
      </div>

      {/* System Stats Grid */}
      {stats && (
        <div className="admin-dashboard-grid mb-4">
          {/* Total Companies */}
          <div className="admin-stat-card">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <div className="stat-label">Total Companies</div>
                <div className="stat-value">{stats.totalCompanies}</div>
                <small className="opacity-75">Active organizations</small>
              </div>
              <i className="bi bi-building stat-icon"></i>
            </div>
          </div>

          {/* Total Users */}
          <div className="admin-stat-card">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <div className="stat-label">Total Users</div>
                <div className="stat-value">{stats.totalUsers.toLocaleString()}</div>
                <small className="opacity-75">{stats.activeUsers} active today</small>
              </div>
              <i className="bi bi-people stat-icon"></i>
            </div>
          </div>

          {/* Revenue */}
          <div className="admin-stat-card">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <div className="stat-label">Total Revenue</div>
                <div className="stat-value">{formatCurrency(stats.totalRevenue)}</div>
                <small className="opacity-75">All-time platform revenue</small>
              </div>
              <i className="bi bi-currency-dollar stat-icon"></i>
            </div>
          </div>

          {/* System Uptime */}
          <div className="admin-stat-card">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <div className="stat-label">System Uptime</div>
                <div className="stat-value">{stats.systemUptime}</div>
                <small className="opacity-75">Last 30 days</small>
              </div>
              <i className="bi bi-heart-pulse stat-icon"></i>
            </div>
          </div>
        </div>
      )}

      <div className="row">
        {/* Recent Activity */}
        <div className="col-lg-8 mb-4">
          <div className="admin-card h-100">
            <div className="card-header bg-white border-0 d-flex align-items-center justify-content-between">
              <h5 className="card-title mb-0 fw-semibold">
                <i className="bi bi-activity me-2 text-primary"></i>
                Recent Activity
              </h5>
              <Link to="/audit-logs" className="btn btn-outline-primary btn-sm">
                View All Logs
              </Link>
            </div>
            <div className="card-body">
              {recentActivity.length > 0 ? (
                <div className="list-group list-group-flush">
                  {recentActivity.map((activity) => (
                    <div key={activity.id} className="list-group-item border-0 px-0 py-3">
                      <div className="d-flex align-items-start">
                        <div className={`me-3 ${getActivityColor(activity.severity)}`}>
                          <i className={`bi ${getActivityIcon(activity.type)}`} style={{ fontSize: '1.25rem' }}></i>
                        </div>
                        <div className="flex-grow-1">
                          <p className="mb-1 fw-medium">{activity.message}</p>
                          <small className="text-muted">
                            <i className="bi bi-clock me-1"></i>
                            {formatTimestamp(activity.timestamp)}
                          </small>
                        </div>
                        <span className={`badge bg-${activity.severity === 'info' ? 'primary' : activity.severity}`}>
                          {activity.severity}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <i className="bi bi-activity text-muted" style={{ fontSize: '3rem' }}></i>
                  <h6 className="mt-3 text-muted">No recent activity</h6>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions & System Status */}
        <div className="col-lg-4">
          {/* Quick Actions */}
          <div className="admin-card mb-4">
            <div className="card-header bg-white border-0">
              <h5 className="card-title mb-0 fw-semibold">
                <i className="bi bi-lightning me-2 text-primary"></i>
                Quick Actions
              </h5>
            </div>
            <div className="card-body">
              <div className="d-grid gap-2">
                <Link to="/companies" className="btn btn-outline-primary">
                  <i className="bi bi-building me-2"></i>
                  Manage Companies
                </Link>
                <Link to="/users" className="btn btn-outline-primary">
                  <i className="bi bi-people me-2"></i>
                  View All Users
                </Link>
                <Link to="/security" className="btn btn-outline-warning">
                  <i className="bi bi-shield-check me-2"></i>
                  Security Monitor
                </Link>
                <Link to="/settings" className="btn btn-outline-secondary">
                  <i className="bi bi-gear me-2"></i>
                  System Settings
                </Link>
              </div>
            </div>
          </div>

          {/* System Status */}
          <div className="admin-card">
            <div className="card-header bg-white border-0">
              <h5 className="card-title mb-0 fw-semibold">
                <i className="bi bi-heart-pulse me-2 text-primary"></i>
                System Status
              </h5>
            </div>
            <div className="card-body">
              {stats && (
                <div className="space-y-3">
                  {/* Storage Usage */}
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <small className="fw-medium">Storage Usage</small>
                      <small className="text-muted">{stats.storageUsed}GB / {stats.storageTotal}GB</small>
                    </div>
                    <div className="progress" style={{ height: '8px' }}>
                      <div 
                        className="progress-bar bg-primary" 
                        style={{ width: `${(stats.storageUsed / stats.storageTotal) * 100}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* System Health Indicators */}
                  <div className="row g-2">
                    <div className="col-6">
                      <div className="text-center p-2 bg-light rounded">
                        <div className="text-success">
                          <i className="bi bi-check-circle-fill"></i>
                        </div>
                        <small className="fw-medium">API</small>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="text-center p-2 bg-light rounded">
                        <div className="text-success">
                          <i className="bi bi-check-circle-fill"></i>
                        </div>
                        <small className="fw-medium">Database</small>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="text-center p-2 bg-light rounded">
                        <div className="text-success">
                          <i className="bi bi-check-circle-fill"></i>
                        </div>
                        <small className="fw-medium">Storage</small>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="text-center p-2 bg-light rounded">
                        <div className="text-warning">
                          <i className="bi bi-exclamation-triangle-fill"></i>
                        </div>
                        <small className="fw-medium">Email</small>
                      </div>
                    </div>
                  </div>

                  <div className="text-center mt-3">
                    <Link to="/health" className="btn btn-outline-primary btn-sm">
                      <i className="bi bi-heart-pulse me-1"></i>
                      View Health Details
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}